const hasText = (value) => Boolean(String(value || '').trim());

const countPortfolioLinks = (profile = {}) =>
  [profile.instagram, profile.spotify, profile.youtube, profile.website_url]
    .filter((value) => hasText(value)).length;

const wordCount = (value) =>
  String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

const compactText = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const looksLikeWebUrl = (value) => /^https?:\/\//i.test(compactText(value));
const looksLikeYouTubeUrl = (value) => {
  const clean = compactText(value).toLowerCase();
  return looksLikeWebUrl(clean) && (clean.includes('youtube.com') || clean.includes('youtu.be'));
};

const firstAvailable = (...values) => values.map(compactText).find(Boolean) || '';

const artistArtForm = (artist = {}) => {
  const rawArtForm = firstAvailable(
    artist.subcategory_other,
    artist.subcategory,
    artist.category_other,
    artist.category,
    'artist'
  );
  return rawArtForm.toLowerCase() === 'other' ? 'artist' : rawArtForm;
};

const listPortfolioLabels = (profile = {}) => {
  const labels = [];
  if (hasText(profile.instagram)) labels.push('Instagram');
  if (hasText(profile.youtube)) labels.push('YouTube');
  if (hasText(profile.spotify)) labels.push('Spotify');
  if (hasText(profile.website_url) || hasText(profile.website)) labels.push('website');
  return labels;
};

export const analyzeArtistProfile = (artist = {}, options = {}) => {
  const videoCount = Number(options.videoCount || artist.video_count || 0);
  const portfolioCount = countPortfolioLinks(artist);
  const bioWords = wordCount(artist.bio);

  const checks = [
    {
      key: 'name',
      label: 'Name',
      complete: hasText(artist.name),
      points: 10,
      suggestion: 'Add the artist name clearly.',
    },
    {
      key: 'category',
      label: 'Art category',
      complete: hasText(artist.category),
      points: 10,
      suggestion: 'Choose the main art category.',
    },
    {
      key: 'subcategory',
      label: 'Speciality',
      complete: hasText(artist.subcategory),
      points: 8,
      suggestion: 'Add a speciality or subcategory so collaborators understand the exact art form.',
    },
    {
      key: 'city',
      label: 'City',
      complete: hasText(artist.city),
      points: 10,
      suggestion: 'Add city or location for better discovery.',
    },
    {
      key: 'email',
      label: 'Email',
      complete: hasText(artist.email),
      points: 7,
      suggestion: 'Add a valid email for account and admin communication.',
    },
    {
      key: 'bio',
      label: 'Bio',
      complete: bioWords >= 20,
      partial: bioWords > 0 && bioWords < 20,
      points: 15,
      partialPoints: 7,
      suggestion: 'Write a stronger bio with art style, experience, city, and what kind of work/collaboration is welcome.',
    },
    {
      key: 'image',
      label: 'Profile image',
      complete: hasText(artist.image_url) || hasText(artist.image),
      points: 15,
      suggestion: 'Upload a clear profile image or performance photo.',
    },
    {
      key: 'portfolio',
      label: 'Portfolio link',
      complete: portfolioCount > 0,
      partial: portfolioCount === 1,
      points: 15,
      partialPoints: 10,
      suggestion: 'Add at least one portfolio link. Two or more links, such as Instagram and YouTube, make the profile stronger.',
    },
    {
      key: 'video',
      label: 'Performance video',
      complete: videoCount > 0 || hasText(artist.youtube),
      points: 10,
      suggestion: 'Add a YouTube performance video or channel so visitors can see the work quickly.',
    },
  ];

  const score = clampScore(checks.reduce((total, check) => {
    if (check.complete) return total + check.points;
    if (check.partial) return total + (check.partialPoints || 0);
    return total;
  }, 0));

  const suggestions = checks
    .filter((check) => !check.complete)
    .map((check) => check.suggestion)
    .slice(0, 4);

  const label = score >= 85
    ? 'Excellent'
    : score >= 70
      ? 'Strong'
      : score >= 50
        ? 'Needs polish'
        : 'Incomplete';

  const tone = score >= 85
    ? 'green'
    : score >= 70
      ? 'teal'
      : score >= 50
        ? 'orange'
        : 'red';

  return {
    score,
    label,
    tone,
    checklist: checks.map((check) => ({
      key: check.key,
      label: check.label,
      complete: check.complete,
      partial: Boolean(!check.complete && check.partial),
      points: check.points,
      suggestion: check.suggestion,
    })),
    suggestions,
    portfolioCount,
    videoCount,
  };
};

export const buildArtistBioSuggestions = (artist = {}) => {
  const name = firstAvailable(artist.name, 'This artist');
  const category = artistArtForm(artist);
  const city = compactText(artist.city);
  const currentBio = compactText(artist.bio);
  const portfolioLabels = listPortfolioLabels(artist);
  const portfolioText = portfolioLabels.length
    ? `Their portfolio is available on ${portfolioLabels.join(', ')}.`
    : 'They are building a stronger public portfolio on KalaManch.Art.';
  const locationText = city ? `${city}-based ` : '';
  const shortCurrentBio = currentBio ? `${currentBio} ` : '';

  return [
    {
      label: 'Professional English bio',
      text: `${name} is a ${locationText}${category} listed on KalaManch.Art. ${shortCurrentBio}${portfolioText}`,
    },
    {
      label: 'Hindi-friendly bio',
      text: `${name} ${city ? `${city} के ` : ''}${category} कलाकार हैं। ${currentBio || 'वे अपने कला कार्य, अनुभव और पोर्टफोलियो को KalaManch.Art पर साझा कर रहे हैं।'}`,
    },
    {
      label: 'Short profile intro',
      text: `${name} is a ${locationText}${category} with a profile and portfolio listed on KalaManch.Art.`,
    },
  ];
};

export const analyzeArtistUploadQuality = (artist = {}, options = {}) => {
  const profileImage = firstAvailable(artist.image_url, artist.image);
  const portfolioImages = Array.isArray(artist.portfolio_image_urls)
    ? artist.portfolio_image_urls.map(compactText).filter(Boolean)
    : [];
  const portfolioVideos = Array.isArray(artist.portfolio_video_urls)
    ? artist.portfolio_video_urls.map(compactText).filter(Boolean)
    : [];
  const portfolioAssets = Array.isArray(artist.portfolio_asset_urls)
    ? artist.portfolio_asset_urls.map(compactText).filter(Boolean)
    : [];
  const totalVideoCount = Number(options.videoCount || 0) + portfolioVideos.length;
  const publicLinks = countPortfolioLinks(artist);
  const invalidImageUrls = portfolioImages.filter((url) => !looksLikeWebUrl(url)).length;
  const invalidVideoUrls = portfolioVideos.filter((url) => !looksLikeYouTubeUrl(url)).length;
  const duplicatePortfolioImages = portfolioImages.length - new Set(portfolioImages).size;
  const duplicatePortfolioVideos = portfolioVideos.length - new Set(portfolioVideos).size;
  const invalidPublicLinks = [artist.instagram, artist.spotify, artist.website_url, artist.website]
    .map(compactText)
    .filter(Boolean)
    .filter((value) => !looksLikeWebUrl(value)).length
    + [artist.youtube].map(compactText).filter(Boolean).filter((value) => !looksLikeYouTubeUrl(value)).length;

  let score = 0;
  const highlights = [];
  const suggestions = [];

  if (profileImage) {
    score += 28;
    highlights.push('Profile image added');
  } else {
    suggestions.push('Add one clear profile image with face or performance focus.');
  }

  if (portfolioImages.length >= 3) {
    score += 26;
    highlights.push(`Strong photo set with ${portfolioImages.length} portfolio photos`);
  } else if (portfolioImages.length === 2) {
    score += 20;
    highlights.push('Good start with 2 portfolio photos');
    suggestions.push('Add one more strong portfolio photo to show range.');
  } else if (portfolioImages.length === 1) {
    score += 12;
    highlights.push('1 portfolio photo added');
    suggestions.push('Add at least 2 more photos showing different looks or performances.');
  } else {
    suggestions.push('Upload portfolio photos so collaborators can judge quality quickly.');
  }

  if (totalVideoCount >= 2) {
    score += 18;
    highlights.push('Multiple video samples added');
  } else if (totalVideoCount === 1 || hasText(artist.youtube)) {
    score += 12;
    highlights.push('At least one video sample is available');
    suggestions.push('A second short performance video can make the profile stronger.');
  } else {
    suggestions.push('Add at least one YouTube performance or sample video.');
  }

  if (publicLinks >= 3) {
    score += 12;
    highlights.push('Public links are well covered');
  } else if (publicLinks >= 1) {
    score += 8;
    highlights.push('Public portfolio links added');
  } else {
    suggestions.push('Add Instagram, Spotify, website, or YouTube so visitors can verify your work.');
  }

  if (portfolioAssets.length > 0) {
    score += 8;
    highlights.push('Extra portfolio assets included');
  }

  if (invalidImageUrls === 0 && invalidVideoUrls === 0 && invalidPublicLinks === 0) {
    score += 8;
    highlights.push('Your media links look clean and usable');
  } else {
    if (invalidImageUrls > 0) {
      suggestions.push('One or more portfolio photo links look invalid. Re-upload or paste the full image URL.');
    }
    if (invalidVideoUrls > 0) {
      suggestions.push('One or more video links are not valid YouTube URLs. Use full YouTube watch, shorts, or channel links.');
    }
    if (invalidPublicLinks > 0) {
      suggestions.push('Check Instagram, Spotify, website, or YouTube links so visitors do not hit broken pages.');
    }
  }

  if (duplicatePortfolioImages > 0 || duplicatePortfolioVideos > 0) {
    suggestions.push('Avoid repeating the same photo or video link. Variety makes the portfolio stronger.');
  }

  if (profileImage) {
    suggestions.push('Use a bright, high-resolution image and avoid blurry or heavily cropped uploads.');
  }
  if (portfolioImages.length >= 1 && portfolioImages.length < 3) {
    suggestions.push('Mix one close-up and one stage or work-action photo for better variety.');
  }

  score = clampScore(score);
  const label = score >= 85
    ? 'Ready to impress'
    : score >= 60
      ? 'Good, with room to polish'
      : 'Needs stronger uploads';
  const tone = score >= 85
    ? 'green'
    : score >= 60
      ? 'orange'
      : 'red';

  return {
    score,
    label,
    tone,
    highlights: highlights.length ? highlights : ['No upload quality signals yet'],
    suggestions: suggestions.filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).slice(0, 4),
  };
};

export const analyzeCollaboratorProfile = (collaborator = {}) => {
  const bioWords = wordCount(collaborator.bio);
  const hasSocialProof = hasText(collaborator.website) || hasText(collaborator.instagram);

  const checks = [
    {
      key: 'organization_name',
      label: 'Organization name',
      complete: hasText(collaborator.organization_name),
      points: 15,
      suggestion: 'Add the organization, brand, group, or client name clearly.',
    },
    {
      key: 'contact_person',
      label: 'Contact person',
      complete: hasText(collaborator.name),
      points: 10,
      suggestion: 'Add a contact person so artists know who they are speaking with.',
    },
    {
      key: 'email',
      label: 'Email',
      complete: hasText(collaborator.email),
      points: 10,
      suggestion: 'Add a valid email for account and admin communication.',
    },
    {
      key: 'organization_type',
      label: 'Organization type',
      complete: hasText(collaborator.organization_type),
      points: 12,
      suggestion: 'Choose the organization type, such as production house, brand, institution, or event organizer.',
    },
    {
      key: 'city',
      label: 'City',
      complete: hasText(collaborator.city),
      points: 10,
      suggestion: 'Add city or location so artists understand where the opportunity may come from.',
    },
    {
      key: 'bio',
      label: 'About / requirement note',
      complete: bioWords >= 18,
      partial: bioWords > 0 && bioWords < 18,
      points: 18,
      partialPoints: 9,
      suggestion: 'Write a clearer profile note about the kind of artists, events, projects, or collaborations you usually look for.',
    },
    {
      key: 'image',
      label: 'Logo / profile image',
      complete: hasText(collaborator.image_url) || hasText(collaborator.image),
      points: 12,
      suggestion: 'Upload a logo or profile image to build trust with artists.',
    },
    {
      key: 'social_proof',
      label: 'Website or Instagram',
      complete: hasSocialProof,
      points: 13,
      suggestion: 'Add a website or Instagram link so artists can understand your work or organization.',
    },
  ];

  const score = clampScore(checks.reduce((total, check) => {
    if (check.complete) return total + check.points;
    if (check.partial) return total + (check.partialPoints || 0);
    return total;
  }, 0));

  const suggestions = checks
    .filter((check) => !check.complete)
    .map((check) => check.suggestion)
    .slice(0, 4);

  const label = score >= 85
    ? 'Excellent'
    : score >= 70
      ? 'Strong'
      : score >= 50
        ? 'Needs polish'
        : 'Incomplete';

  const tone = score >= 85
    ? 'green'
    : score >= 70
      ? 'teal'
      : score >= 50
        ? 'orange'
        : 'red';

  return {
    score,
    label,
    tone,
    checklist: checks.map((check) => ({
      key: check.key,
      label: check.label,
      complete: check.complete,
      partial: Boolean(!check.complete && check.partial),
      points: check.points,
      suggestion: check.suggestion,
    })),
    suggestions,
  };
};

export const buildCollaboratorBioSuggestions = (collaborator = {}) => {
  const organization = firstAvailable(collaborator.organization_name, collaborator.name, 'This collaborator');
  const type = firstAvailable(collaborator.organization_type, 'creative organization');
  const city = compactText(collaborator.city);
  const currentBio = compactText(collaborator.bio);
  const focusText = currentBio || 'They connect with artists for performances, campaigns, events, productions, and creative projects.';
  const locationText = city ? `${city}-based ` : '';

  return [
    {
      label: 'Professional English bio',
      text: `${organization} is a ${locationText}${type} on KalaManch.Art. ${focusText} Artists can connect for relevant opportunities, enquiries, and private message conversations through KalaManch.Art.`,
    },
    {
      label: 'Hindi-friendly bio',
      text: `${organization} ${city ? `${city} में स्थित ` : ''}${type} है। ${currentBio || 'यह कलाकारों से प्रदर्शन, इवेंट, प्रोडक्शन, कैंपेन और रचनात्मक प्रोजेक्ट्स के लिए जुड़ता है।'} कलाकार KalaManch.Art के माध्यम से सीधे संपर्क कर सकते हैं।`,
    },
    {
      label: 'Short profile intro',
      text: `${organization} is looking to connect with suitable artists for creative work, events, collaborations, and project enquiries on KalaManch.Art.`,
    },
  ];
};

export const profileScoreBarClass = (tone) => {
  if (tone === 'green') return 'from-green-500 to-emerald-500';
  if (tone === 'teal') return 'from-[#00BFA5] to-[#0F766E]';
  if (tone === 'orange') return 'from-[#FF6F00] to-[#C45A00]';
  return 'from-red-500 to-rose-600';
};

export const profileScoreBadgeClass = (tone) => {
  if (tone === 'green') return 'border-green-200 bg-green-50 text-green-800';
  if (tone === 'teal') return 'border-teal-200 bg-teal-50 text-teal-800';
  if (tone === 'orange') return 'border-orange-200 bg-orange-50 text-orange-800';
  return 'border-red-200 bg-red-50 text-red-800';
};
